// Input mask for credit fields that accept a fractional value (contract credits
// are decimal(10,2) in the backend). Unlike JustNumber — which strips the
// decimal separator while the user is still typing — this keeps the separator,
// so `126.79` can actually be typed.
const DEFAULT_MAX_DECIMALS = 2;

// pt-BR keyboards produce a comma: when there is one, it is the decimal
// separator and any dot is a thousands separator (same heuristic as
// parseMoneyValue in src/utils/parse-money.ts).
const normalizeSeparator = (value: string) =>
  value.includes(",") ? value.replace(/\./g, "").replace(",", ".") : value;

export const JustDecimal = (
  value: string,
  maxDecimals = DEFAULT_MAX_DECIMALS,
) => {
  const numericValue = normalizeSeparator(value).replace(/[^0-9.]/g, "");
  const [integerPart, ...decimalParts] = numericValue.split(".");

  // No separator typed yet — plain digits.
  if (!decimalParts.length) {
    return integerPart;
  }

  // A trailing "." is preserved on purpose: it is the intermediate state of
  // typing "126." before "126.79". Extra dots are dropped, not treated as a
  // new separator ("1.2.3" -> "1.23").
  return `${integerPart}.${decimalParts.join("").slice(0, maxDecimals)}`;
};

// Numeric-value checks for the form schemas. They run on the string the mask
// produces, so `Number()`-coercible-but-not-decimal values ("1e3", "0x1f",
// "Infinity", "-5", whitespace) are rejected, and the intermediate "126." is
// accepted (the forms validate onBlur).
export const isDecimalString = (value: string) => /^\d+(\.\d*)?$/.test(value);

export const hasMaxDecimals = (
  value: string,
  maxDecimals = DEFAULT_MAX_DECIMALS,
) => new RegExp(`^\\d+(\\.\\d{0,${maxDecimals}})?$`).test(value);
